import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import { createCompanySetup } from "@services/companySetup";
import { logoutLocal, updateProfile } from "@services/auth";
import { getCurrentUser } from "@utils/userUtils";

type Props = {
  open: boolean;
  prefillEmail?: string | null;
  onCreated: () => void;
};

export default function SetupCompanyDialog({
  open,
  prefillEmail,
  onCreated,
}: Props) {
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (!open) return;
    const user = getCurrentUser();
    setCompanyName("");
    setPhone("");
    setEmail((prefillEmail || user?.email || "").trim());
    setError("");
    setSubmitting(false);
  }, [open, prefillEmail]);

  const handleSubmit = async () => {
    const name = companyName.trim();
    const phoneVal = phone.trim();
    const emailVal = email.trim();
    if (!name || !phoneVal || !emailVal) {
      setError("Company name, phone, and email are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await createCompanySetup({
        company_name: name,
        phone: phoneVal,
        email: emailVal,
      });
      // Same phone on user profile (billing company phone → /auth/me).
      try {
        await updateProfile({ phone_number: phoneVal });
      } catch {
        // Company already created; profile sync must not block setup.
      }
      onCreated();
    } catch (e: any) {
      setError(e?.message || "Failed to create company");
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logoutLocal();
    } catch {
      // still redirect
    }
    const target =
      process.env.REACT_APP_POST_LOGOUT_REDIRECT_URL || window.location.origin;
    window.location.replace(target);
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 4 }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "16px",
          backgroundColor: "#fffdfd",
          border: "1px solid #ccf5ff",
          boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
        },
      }}
    >
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography
          sx={{
            fontFamily: '"Rethink Sans", sans-serif',
            fontWeight: 700,
            fontSize: "22px",
            color: "#065b70",
          }}
        >
          Set up your Company
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "#404040" }}>
          Create your company before adding a facility. Company name is used for
          billing, not the facility name.
        </Typography>
        <TextField
          label="Company name"
          required
          fullWidth
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Phone"
          required
          fullWidth
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          inputProps={{ maxLength: 30 }}
        />
        <TextField
          label="Email"
          required
          fullWidth
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          inputProps={{ maxLength: 255 }}
        />
        {error ? (
          <Typography sx={{ color: "#dc2626", fontSize: "13px" }}>{error}</Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            pt: 1,
            flexWrap: "wrap",
          }}
        >
          <Button
            onClick={handleSignOut}
            disabled={submitting || signingOut}
            sx={{
              background: "#ffffff",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              padding: "12px 24px",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 500,
              fontSize: "14px",
              color: "#262626",
              textTransform: "none",
              "&:hover": { background: "#F9FAFB" },
            }}
          >
            {signingOut ? <CircularProgress size={18} /> : "Logout"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || signingOut}
            sx={{
              background: "linear-gradient(to right, #0a829d, #58c2d8)",
              borderRadius: "8px",
              padding: "12px 24px",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#e6fafc",
              textTransform: "none",
              "&:hover": { opacity: 0.9 },
            }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create company"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
